import {Component, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {environment} from '~local/environments/environment';
import {TranslateService} from '@ngx-translate/core';
import {Angulartics2GoogleAnalytics} from 'angulartics2';
import * as countries from 'i18n-iso-countries';
import {merge} from 'rxjs';
import {filter, map, mergeMap} from 'rxjs/operators';
import {I18nService} from './core/services/I18nService';
import {Logger} from './core/services/LoggerService';
import {IconService} from './shared/services/IconService';

const log = new Logger('App');

@Component({
    standalone: true,
    selector: 'app-root',
    template: `<router-outlet/>`,
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private translateService: TranslateService,
        private i18nService: I18nService,
        private angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
        private renderer: Renderer2,
        private iconService: IconService
    ) {
        if (environment.production) {
            this.angulartics2GoogleAnalytics.startTracking();
        }
    }

    ngOnInit() {
        // Setup logger
        if (environment.production) {
            Logger.enableProductionMode();
        }

        if (environment.streeboScript !== undefined) {
            this.addJsToElement(environment.streeboScript);
        }

        // Setup translations
        this.i18nService.init(environment.defaultLanguage, environment.supportedLanguages);
        const onNavigationEnd = this.router.events.pipe(filter(event => event instanceof NavigationEnd));

        countries.registerLocale(require('i18n-iso-countries/langs/en.json'));

        // Change page title on navigation or language change, based on route data
        merge(this.translateService.onLangChange, onNavigationEnd)
            .pipe(
                map(() => {
                    let route = this.activatedRoute;
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                mergeMap(route => route.data),
            )
            .subscribe(event => {
                const title = event['title'];
                if (title) {
                    this.titleService.setTitle(this.translateService.instant('APP_NAME'));
                }
            });

        // Load custom icons
        this.iconService.loadCustomIcons();

    }

    ngOnDestroy() {
        this.i18nService.destroy();
    }

    addJsToElement(src: string): HTMLScriptElement {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;
        this.renderer.appendChild(document.body, script);
        return script;
    }
}
